import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AutoSizer from '../common/virtualized/autoSizer';
import { FixedSizeList as List } from 'react-window';
import { DragType } from '../../models/dragTypes';
import Playlist from '../../models/playlist/playlist';
import PlaylistItem from '../../models/playlistItem/playlistItem';
import QueryIterator from '../../models/queryIterator';
import SoundCloudApiTrack from '../../models/soundcloud/soundCloudApiTrack';
import SoundCloudPlaylist from '../../models/soundcloud/soundCloudPlaylist';
import Source from '../../models/source';
import Track from '../../models/track';
import SoundCloudTrack from '../../models/track/soundCloudTrack';
import { ActivePlaylistContext } from '../../services/activePlaylist/activePlaylist';
import { ColumnScope } from '../../services/columnSettings/playlistColumns';
import { createAddItemsToPlaylistThenLibrary } from '../../services/library/addTracksToPlaylistThenLibrary';
import { LibraryContext } from '../../services/library/libraryService';
import useSearchBox from '../../services/shortcuts/searchBoxes';
import {
  SmallDeviceContext,
  SmallDeviceShow,
} from '../../services/smallDevices/smallDeviceService';
import { isSoundCloudUrl } from '../../services/soundCloud/soundCloudApi';
import { SoundCloudContext } from '../../services/soundCloud/soundCloudService';
import { SyncContext } from '../../services/synchronization/syncService';
import { Context } from '../../services/_globalContext/context';
import Constants from '../../settings';
import createAddPlaylistToPlaylist from '../../utilities/helperFactories/addPlaylistToPlaylist';
import { preventDefaultStopPropogation } from '../../utilities/preventDefault';
import AuthorizeButton from '../common/authorizeButton/authorizeButton';
import FadilaMenuItem from '../common/contextMenu/fadilaMenuItem';
import ErrorBoundary from '../common/errorBoundary/errorBoundary';
import Spinner from '../common/spinner/spinner';
import { StreamingServicePlaylist } from '../common/streamingServiceCommon/streamSvcPlaylistPlaylistRow';
import StreamingServicePlaylistRowRenderer, {
  StreamingServicePlaylistRowData,
} from '../common/streamingServiceCommon/streamSvcPlaylistPlaylistRowRenderer';
import { createTrackSortValue } from '../common/streamingServiceCommon/searchResultSorting';
import StreamingServiceSearchResults from '../common/streamingServiceCommon/streamSvcSearchResults';
import useLoadMore from '../common/virtualized/useLoadMore';
import LibraryPlaylists from '../library/libraryPlaylists';
import libPlsStyles from '../library/libraryPlaylists.module.scss';
import TrackPlaylistComponent from '../playlist/trackPlaylistComponent';
import { openOnSoundCloud } from './openOnSoundCloud';
import PlaylistSearchResult, {
  SoundCloudPlaylistRowProps,
} from './searchResults/playlistSearchResult';
import TrackSearchResult, { SoundCloudTrackRowProps } from './searchResults/trackSearchResult';
import s from './soundCloud.module.scss';
import {
  SoundCloudPlaylistDragHelper,
  soundCloudPlaylistDragHelperKey,
  SoundCloudPlaylistListItem,
  toListItem,
} from './soundCloudPlaylistListItem';
import SoundCloudPlaylistRowContent from './soundCloudPlaylistRowContent';
import SoundCloudSyncSubMenu from './soundCloudSyncSubMenu';

/**
 * Sorting for the track results. Module scope, so the per-item cache inside
 * outlives a render - see searchResultSorting.ts.
 */
const trackSortValue = createTrackSortValue<SoundCloudApiTrack>(SoundCloudTrack.from);

enum SoundCloudSearchType {
  Track = 'track',
  Playlist = 'playlist',
}

/** What the middle track list is showing: the user's likes, or one playlist. */
interface TrackListSource {
  id: string;
  name: string;
  /** Set when the list is a playlist from the right-hand column, for the highlight. */
  playlist?: SoundCloudPlaylistListItem;
}

const likesSource: TrackListSource = {
  id: 'soundcloud:me:likes',
  name: 'Likes',
};

interface Props {}

/**
 * The SoundCloud page: sign in, then likes and playlists on the account,
 * search, a pasted soundcloud.com link, and from any of those add to the
 * library and to playlists, or play.
 *
 * The Spotify page trimmed: SoundCloud has no albums or artists to search,
 * no player SDK, and its lists have no totals - so the two track lists
 * (likes and a playlist) share one set of state and one loader, and the
 * search has two shapes rather than four.
 */
export default React.memo(function SoundCloudComponent({}: Props) {
  const ctx = useContext(Context);
  const queryRef = useSearchBox('search');
  const smallDevSvc = useContext(SmallDeviceContext);
  const activePlaylist = useContext(ActivePlaylistContext);
  const libCtx = useContext(LibraryContext);
  const scCtx = useContext(SoundCloudContext);
  const syncCtx = useContext(SyncContext);
  const api = scCtx.soundCloudApi;

  // Search State
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchType, setSearchType] = useState<SoundCloudSearchType>();

  const [searchTracksIterator, setSearchTracksIterator] =
    useState<QueryIterator<SoundCloudApiTrack>>();
  const [searchTracksResults, setSearchTracksResults] = useState<SoundCloudApiTrack[]>([]);
  const [searchTracksLoadingMore, searchTracksLoadMore] = useLoadMore({
    items: searchTracksResults,
    setItems: setSearchTracksResults,
    queryIterator: searchTracksIterator,
    setIterator: setSearchTracksIterator,
  });

  const [searchPlaylistsIterator, setSearchPlaylistsIterator] =
    useState<QueryIterator<SoundCloudPlaylist>>();
  const [searchPlaylistsResults, setSearchPlaylistsResults] = useState<SoundCloudPlaylist[]>([]);
  const [searchPlaylistsLoadingMore, searchPlaylistsLoadMore] = useLoadMore({
    items: searchPlaylistsResults,
    setItems: setSearchPlaylistsResults,
    queryIterator: searchPlaylistsIterator,
    setIterator: setSearchPlaylistsIterator,
  });
  // Search State End

  // Track list State - the likes, or one playlist
  const [listSource, setListSource] = useState<TrackListSource>();
  const [listIterator, setListIterator] = useState<QueryIterator<SoundCloudApiTrack>>();
  const [listTracks, setListTracks] = useState<Track[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listLoadingMore, listLoadMore] = useLoadMore<Track, SoundCloudApiTrack>({
    items: listTracks,
    setItems: setListTracks,
    queryIterator: listIterator,
    setIterator: setListIterator,
    itemMapper: SoundCloudTrack.from,
  });
  // Track list State End

  const listPlaylistItems = useMemo(() => listTracks.map(PlaylistItem.from), [listTracks]);
  const playlist = useMemo(
    () =>
      listSource
        ? new Playlist(listSource.name, listPlaylistItems, listSource.id)
        : new Playlist('Null', [], 'Null'),
    // A new source always comes with a new tracks array (openTrackList sets
    // both), so watching the tracks is enough - and watching the source too
    // would build a playlist that briefly pairs the new name with the old
    // tracks. Same reasoning as the Spotify page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listPlaylistItems],
  );

  // Playlists State - the right-hand column
  const [playlists, setPlaylists] = useState<SoundCloudPlaylistListItem[]>([]);
  const [playlistsLoading, setPlaylistsLoading] = useState(false);
  const [playlistsLoaded, setPlaylistsLoaded] = useState(false);
  const [playlistsElement, setPlaylistsElement] = useState<HTMLElement | null>(null);
  // Playlists State End

  const clearSearch = useCallback(function clearSearch() {
    setSearchType(undefined);
    setSearchTracksIterator(undefined);
    setSearchTracksResults([]);
    setSearchPlaylistsIterator(undefined);
    setSearchPlaylistsResults([]);
  }, []);

  const clearTrackList = useCallback(function clearTrackList() {
    setListSource(undefined);
    setListIterator(undefined);
    setListTracks([]);
  }, []);

  /**
   * Errors are already on the snackbar by the time they get here: every
   * call goes through soundCloudService's wrapper, which reports and
   * rethrows. So the catches below only stop the rejection reaching the
   * console as unhandled.
   */
  const openTrackList = useCallback(
    async function openTrackList(
      source: TrackListSource,
      fetchFirstPage: () => Promise<QueryIterator<SoundCloudApiTrack>>,
    ) {
      clearSearch();
      setListIterator(undefined);
      setListLoading(true);

      try {
        const iterator = await fetchFirstPage();

        setListSource(source);
        setListTracks(iterator.results.map(SoundCloudTrack.from));
        setListIterator(iterator);
      } catch (err) {
        console.debug(err);
      } finally {
        setListLoading(false);
      }
    },
    [clearSearch],
  );

  const openLikes = useCallback(
    function openLikes() {
      if (api) {
        return openTrackList(likesSource, () => api.getLikedTracks());
      }
      return Promise.resolve();
    },
    [api, openTrackList],
  );

  const openPlaylist = useCallback(
    function openPlaylist(pl: SoundCloudPlaylistListItem) {
      if (api) {
        return openTrackList({ id: pl.id, name: pl.title, playlist: pl }, () =>
          api.getPlaylistTracks(pl.id),
        );
      }
      return Promise.resolve();
    },
    [api, openTrackList],
  );

  const openApiPlaylist = useCallback(
    function openApiPlaylist(pl: SoundCloudPlaylist) {
      return openPlaylist(toListItem(pl));
    },
    [openPlaylist],
  );

  const getUserPlaylists = useCallback(
    async function getUserPlaylists() {
      if (!api) {
        return;
      }

      setPlaylistsLoading(true);

      try {
        const all: SoundCloudPlaylist[] = [];
        let iterator = await api.getUserPlaylists();

        all.push(...iterator.results);

        while (iterator.next) {
          iterator = await iterator.next();

          all.push(...iterator.results);
        }

        setPlaylists(all.map(toListItem));
        setPlaylistsLoaded(true);
      } catch (err) {
        console.debug(err);
      } finally {
        setPlaylistsLoading(false);
      }
    },
    [api],
  );

  /**
   * Opening the page syncs every playlist linked to SoundCloud, throttled
   * inside syncPlaylist. The YouTube page does the same for its own; between
   * them, a playlist linked to both settles whichever page you visit.
   */
  useEffect(
    function syncLinkedPlaylists() {
      if (syncCtx.canSyncSoundCloud) {
        syncCtx.syncAllSoundCloud();
      }
      // syncAllSoundCloud is stable; syncCtx as a whole changes as syncs run.
    },
    [syncCtx.canSyncSoundCloud],
  );

  useEffect(
    function loadUser() {
      if (scCtx.accessToken) {
        getUserPlaylists();
        openLikes();
      } else {
        setPlaylists([]);
        setPlaylistsLoaded(false);
        clearTrackList();
        clearSearch();
      }
      // The api object is remade with the token, so the token is the one
      // dependency that matters; the callbacks above follow from it.
    },
    [scCtx.accessToken],
  );

  const handleSearch = useCallback(
    async function handleSearch(ev: React.FormEvent<HTMLFormElement>) {
      ev.preventDefault();

      if (!api) {
        return;
      }

      const form = ev.currentTarget;
      const query = (form.elements.namedItem('query') as HTMLInputElement).value.trim();
      const type = (form.elements.namedItem('type') as HTMLInputElement)
        .value as SoundCloudSearchType;

      if (!query) {
        return;
      }

      if (isSoundCloudUrl(query)) {
        // A pasted link. Resolve it and show the one thing it names.
        clearTrackList();
        clearSearch();
        setSearchLoading(true);

        try {
          const resource = await api.resolve(query);

          switch (resource.kind) {
            case 'track': {
              const track = resource as SoundCloudApiTrack;

              setSearchType(SoundCloudSearchType.Track);
              setSearchTracksResults([track]);
              setSearchTracksIterator({ results: [track] });
              break;
            }

            case 'playlist':
              await openApiPlaylist(resource as SoundCloudPlaylist);
              break;

            default:
              ctx.callSnackbar(
                'That link is not a track or a playlist. Paste a track or playlist link.',
              );
              break;
          }
        } catch (err) {
          console.debug(err);
        } finally {
          setSearchLoading(false);
        }

        return;
      }

      clearTrackList();
      setSearchTracksIterator(undefined);
      setSearchPlaylistsIterator(undefined);
      setSearchType(type);
      setSearchLoading(true);

      try {
        if (type === SoundCloudSearchType.Playlist) {
          const res = await api.searchPlaylists(query, Constants.ResultCount);

          setSearchPlaylistsResults(res.results);
          setSearchPlaylistsIterator(res);
        } else {
          const res = await api.searchTracks(query, Constants.ResultCount);

          setSearchTracksResults(res.results);
          setSearchTracksIterator(res);
        }
      } catch (err) {
        console.debug(err);
      } finally {
        setSearchLoading(false);
      }
    },
    [api, ctx, clearSearch, clearTrackList, openApiPlaylist],
  );

  const addItemsToPlaylistThenLibrary = useMemo(
    () =>
      createAddItemsToPlaylistThenLibrary<SoundCloudApiTrack>(ctx, libCtx, SoundCloudTrack.from),
    [ctx, libCtx],
  );

  /**
   * For playing single tracks from search results.
   * In those cases we do not add to library.
   */
  const playSearchResult = useCallback(
    function playSearchResult(item: SoundCloudApiTrack) {
      const plItm = new PlaylistItem(item.urn, Source.SoundCloud);

      const doPlay = true;
      activePlaylist.setActivePlaylist([plItm], 0, doPlay);
    },
    [activePlaylist],
  );

  const addPlaylistToPlaylist = useMemo(
    () => (addItems: (i: PlaylistItem[]) => void) =>
      createAddPlaylistToPlaylist<SoundCloudApiTrack>(
        ctx,
        api?.getPlaylistTracks!,
        addItemsToPlaylistThenLibrary,
        addItems,
      ),
    [ctx, api, addItemsToPlaylistThenLibrary],
  );

  /**
   * Lets a SoundCloud playlist dropped on a library playlist be expanded
   * into tracks by playlistRowCommon.ts, which has no SoundCloud context of
   * its own - see soundCloudPlaylistListItem.ts.
   */
  useEffect(
    function registerPlaylistDragHelper() {
      if (!api) {
        return;
      }

      const helper: SoundCloudPlaylistDragHelper = { addPlaylistToPlaylist };
      const dndData = ctx.dndData;

      dndData[soundCloudPlaylistDragHelperKey] = helper;

      return function unregister() {
        if (dndData[soundCloudPlaylistDragHelperKey] === helper) {
          delete dndData[soundCloudPlaylistDragHelperKey];
        }
      };
    },
    [api, addPlaylistToPlaylist, ctx.dndData],
  );

  const titleAccessor = useCallback((pl: SoundCloudPlaylistListItem) => pl.title, []);
  const additionalMenuItems = useCallback(
    (pl: SoundCloudPlaylistListItem) => (
      <>
        <SoundCloudSyncSubMenu playlistUrn={pl.id} title={pl.title} />
        <FadilaMenuItem
          onClick={preventDefaultStopPropogation(() => openOnSoundCloud(pl.permalinkUrl))}
        >
          Open on SoundCloud
        </FadilaMenuItem>
      </>
    ),
    [],
  );
  const itemData: StreamingServicePlaylistRowData<SoundCloudPlaylistListItem> = useMemo(
    () => ({
      curUserPl: listSource && listSource.playlist,
      playlists,
      dragType: DragType.SoundCloudPlaylist,
      getPlaylistItems: openPlaylist,
      addPlaylistToPlaylist,
      titleAccessor,
      additionalContent: SoundCloudPlaylistRowContent,
      additionalMenuItems,
      portalTarget: playlistsElement || undefined,
    }),
    [
      listSource,
      playlists,
      openPlaylist,
      addPlaylistToPlaylist,
      titleAccessor,
      additionalMenuItems,
      playlistsElement,
    ],
  );

  const trackSearchProps: Partial<SoundCloudTrackRowProps> = useMemo(
    () => ({
      handlePlayTrack: playSearchResult,
      addItemsThenToLibrary: addItemsToPlaylistThenLibrary,
    }),
    [playSearchResult, addItemsToPlaylistThenLibrary],
  );

  const playlistSearchProps: Partial<SoundCloudPlaylistRowProps> = useMemo(
    () => ({
      addPlaylistToPlaylist,
      openPlaylist: openApiPlaylist,
    }),
    [addPlaylistToPlaylist, openApiPlaylist],
  );

  return (
    <ErrorBoundary>
      {!scCtx.accessToken ? (
        <div>
          <AuthorizeButton
            busy={scCtx.isAuthorizing}
            onClick={() => scCtx.authorize({ allowRedirect: true }).catch(ctx.callSnackbar)}
          >
            SoundCloud Login
          </AuthorizeButton>
        </div>
      ) : (
        <article className="grid-container full fluid">
          <div className="grid-x">
            <div className="cell shrink">
              <article className={`mainViewport ${ctx.playerSize}`}>
                <LibraryPlaylists />
              </article>
            </div>

            <div className="cell auto">
              <div className={`mainViewport ${ctx.playerSize}`}>
                <section>
                  <form onSubmit={handleSearch}>
                    <div className={`grid-x ${s.searchBar}`}>
                      <label className={s.searchLabel} htmlFor="query">
                        <div className="grid-x grid-margin-x">
                          <input
                            className="cell auto"
                            type="text"
                            name="query"
                            ref={queryRef}
                            id="query"
                            placeholder="Search SoundCloud, or paste a soundcloud.com link"
                          />
                          <div className={classNames('cell', 'shrink', s.searchTypeControls)}>
                            <label htmlFor={SoundCloudSearchType.Track}>
                              Tracks
                              <input
                                type="radio"
                                id={SoundCloudSearchType.Track}
                                name="type"
                                value={SoundCloudSearchType.Track}
                                defaultChecked
                              />
                            </label>
                            <label htmlFor={SoundCloudSearchType.Playlist}>
                              Playlists
                              <input
                                type="radio"
                                id={SoundCloudSearchType.Playlist}
                                name="type"
                                value={SoundCloudSearchType.Playlist}
                              />
                            </label>
                          </div>
                          <button className="cell shrink button">Search</button>
                        </div>
                      </label>
                      <button
                        type="button"
                        className={classNames('button', 'secondary', s.likesButton)}
                        disabled={listSource === likesSource && !searchType}
                        onClick={openLikes}
                      >
                        Likes
                      </button>
                    </div>
                  </form>
                </section>

                {searchType === undefined && (
                  <div className={`items-list ${s.mainViewContainer}`}>
                    {(listLoading || listIterator !== undefined) && listSource && (
                      <TrackPlaylistComponent
                        moveable={false}
                        dragType={DragType.TrackItems}

                        playlist={playlist}
                        trackItems={listTracks}
                        loading={listLoading}

                        display={listIterator !== undefined}
                        isComplete={() => !listIterator?.next}
                        // SoundCloud pages carry no total; TrackPlaylist sizes
                        // itself by what has loaded when this is undefined.
                        totalItemCount={() => listIterator?.totalResults!}

                        columnScope={ColumnScope.SoundCloud}

                        loadMore={listLoadMore}
                        hasMore={listIterator?.next !== undefined}
                        isNextPageLoading={listLoadingMore}
                      />
                    )}
                  </div>
                )}

                {searchType === SoundCloudSearchType.Track && (
                  <div className={`items-list ${s.mainViewContainer}`}>
                    {(searchLoading || searchTracksIterator !== undefined) && (
                      <StreamingServiceSearchResults<SoundCloudApiTrack, SoundCloudTrackRowProps>
                        columnScope={ColumnScope.SoundCloudTrackSearch}
                        itemComponent={TrackSearchResult}
                        results={searchTracksResults}
                        loading={searchLoading}
                        display={searchTracksIterator !== undefined}
                        isComplete={() => !searchTracksIterator?.next}

                        totalItemCount={() => searchTracksIterator?.totalResults!}
                        totalItemCountString={false}

                        isNextPageLoading={searchTracksLoadingMore}
                        loadMore={searchTracksLoadMore}
                        hasMore={searchTracksIterator?.next !== undefined}

                        itemSize={26}
                        sortValue={trackSortValue}
                        props={trackSearchProps}
                      />
                    )}
                  </div>
                )}

                {searchType === SoundCloudSearchType.Playlist && (
                  <div className={`items-list ${s.mainViewContainer}`}>
                    {(searchLoading || searchPlaylistsIterator !== undefined) && (
                      <StreamingServiceSearchResults<SoundCloudPlaylist, SoundCloudPlaylistRowProps>
                        columnScope={ColumnScope.SoundCloudPlaylistSearch}
                        itemComponent={PlaylistSearchResult}
                        results={searchPlaylistsResults}
                        loading={searchLoading}
                        display={searchPlaylistsIterator !== undefined}
                        isComplete={() => !searchPlaylistsIterator?.next}

                        totalItemCount={() => searchPlaylistsIterator?.totalResults!}
                        totalItemCountString={false}

                        isNextPageLoading={searchPlaylistsLoadingMore}
                        loadMore={searchPlaylistsLoadMore}
                        hasMore={searchPlaylistsIterator?.next !== undefined}

                        props={playlistSearchProps}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="cell shrink">
              {playlistsLoading ? (
                <Spinner />
              ) : (
                playlistsLoaded && (
                  <div className={`mainViewport ${ctx.playerSize}`}>
                    <article
                      ref={(el) => setPlaylistsElement(el)}
                      className={classNames(libPlsStyles.playlistList, s.soundCloudPlaylists, {
                        [libPlsStyles.open]:
                          smallDevSvc.state === SmallDeviceShow.StreamingPlaylists,
                      })}
                    >
                      {playlistsElement !== null && (
                        <AutoSizer>
                          {({ height, width }) => (
                            <List
                              height={height}
                              width={width}
                              itemCount={playlists.length}
                              itemSize={29}
                              // The renderer is fixed to the StreamingServicePlaylist
                              // union; the row data is invariant in T, so the
                              // narrower SoundCloud type needs widening here -
                              // the same cast the Spotify page makes.
                              itemData={
                                itemData as StreamingServicePlaylistRowData<StreamingServicePlaylist>
                              }
                            >
                              {StreamingServicePlaylistRowRenderer}
                            </List>
                          )}
                        </AutoSizer>
                      )}
                    </article>
                  </div>
                )
              )}
            </div>
          </div>
        </article>
      )}
    </ErrorBoundary>
  );
});
